// src/services/UserValidationService.ts
import { doc, getDoc } from 'firebase/firestore';
import { getForbiddenUsernameWords } from '../data/moderation';
import { firestore } from '../config/firebase';

interface UsernameValidationResult {
    isAvailable: boolean;
    isValid: boolean;
    message: string;
}

// Estonian bad words and vulgar content (expandable list)
/**
 * Check if username contains forbidden words
 */
const containsForbiddenWords = (username: string): boolean => {
    const lowerUsername = username.toLowerCase();
    return getForbiddenUsernameWords().some(word => lowerUsername.includes(word.toLowerCase()));
};

/**
 * Check if username is available and valid
 */
export const validateUsername = async (username: string): Promise<UsernameValidationResult> => {
    try {
        const cleanUsername = username.trim();

        // Basic validation first
        if (cleanUsername.length < 3) {
            return {
                isAvailable: false,
                isValid: false,
                message: 'Kasutajanimi peab olema vähemalt 3 tähemärki pikk'
            };
        }

        if (cleanUsername.length > 20) {
            return {
                isAvailable: false,
                isValid: false,
                message: 'Kasutajanimi ei tohi olla pikem kui 20 tähemärki'
            };
        }

        if (!/^[a-zA-Z0-9_]+$/.test(cleanUsername)) {
            return {
                isAvailable: false,
                isValid: false,
                message: 'Kasutajanimi tohib sisaldada ainult tähti, numbreid ja alakriipsu'
            };
        }

        // Check for forbidden words
        if (containsForbiddenWords(cleanUsername)) {
            return {
                isAvailable: false,
                isValid: false,
                message: 'Kasutajanimi sisaldab sobimatud sõnu'
            };
        }

        // Check the public reservation collection. This used to run up to three
        // queries against `users` -- including downloading every user document, and with
        // it every email address -- on each keystroke. One document read replaces it.
        const reservation = await getDoc(doc(firestore, 'usernames', cleanUsername.toLowerCase()));
        if (reservation.exists()) {
            return {
                isAvailable: false,
                isValid: true,
                message: 'See kasutajanimi on juba kasutusel'
            };
        }

        return {
            isAvailable: true,
            isValid: true,
            message: 'Kasutajanimi on saadaval'
        };

    } catch (error) {
        console.error('Error validating username:', error);
        return {
            isAvailable: false,
            isValid: false,
            message: 'Viga kasutajanime kontrollimisel'
        };
    }
};
